<template>
    <dx-data-grid
        ref="dataGridRef"
        :allow-column-reordering="true"
        :column-chooser="{ enabled: true, mode: 'select' }"
        :column-min-width="50"
        :columns="columns"
        :data-source="dataSource"
        :filter-panel="{ visible: true }"
        :filter-row="{ visible: true }"
        :header-filter="{ visible: true }"
        :height="50"
        :hover-state-enabled="true"
        :remote-operations="{ filtering: true, paging: true, sorting: true, summary: true }"
        :repaint-changes-only="true"
        :row-alternation-enabled="true"
        :show-borders="true"
        :show-column-lines="true"
        :width="'100%'"
        :word-wrap-enabled="true"
        style="flex-grow: 1"
        value-expr="id"
        @toolbar-preparing="onToolbarPreparing($event)"
        @on-cell-prepared="onCellPrepared($event)"
        @content-ready="onContentReady($event)"
    >
        <dx-paging :enabled="true" :page-size="40" />
        <dx-load-panel :enabled="true" />
        <dx-scrolling mode="virtual" />
        <dx-sorting mode="single" />
        <dx-export :allow-export-selected-data="true" :enabled="true" :formats="['pdf', 'excel']" />
        <dx-editing
            :allow-deleting="true"
            :allow-updating="true"
            :refresh-mode="'full'"
            mode="batch"
        />
        <template #startDate="{ data: rowData }">
            <mped-calculation-period-component
                :default-value="rowData.data.start_date"
                :show-clear-button="true"
                :pos-year="5"
                selection-mode="single"
                @value-changed="onStartDateChanged($event, rowData)"
            />
        </template>
        <template #endDate="{ data: rowData }">
            <mped-calculation-period-component
                :default-value="rowData.data.end_date"
                :show-clear-button="true"
                :pos-year="5"
                selection-mode="single"
                @value-changed="onEndDateChanged($event, rowData)"
            />
        </template>
        <template #chooseMvp="{ data: rowData }">
            <DxDropDownBox
                :data-source="lookupData"
                :value="rowData.data.mvp_code"
                value-expr="mvp_code"
                display-expr="mvp_code"
                :show-clear-button="true"
                :defer-rendering="false"
            >
                <DxTreeList
                    :data-source="lookupData"
                    :columns="gridColumns"
                    :filter-row="{ visible: true }"
                    :focused-row-enabled="true"
                    :key-expr="'mvp_code'"
                    height="300px"
                    selection-mode="single"
                    @focused-row-changed="(e) => onMvpSelectionChanged(e, rowData)"
                />
            </DxDropDownBox>
        </template>
        <template #checkbox>
            <dx-check-box
                :value="checkbox"
                text="Показывать архив"
                @value-changed="onCheckboxChanged"
            />
        </template>
    </dx-data-grid>
</template>

<script>
export default {
    name: 'mped-amortization-distribution-view',
};
</script>

<script setup>
import { onBeforeMount, ref } from 'vue';
import {
    DxDataGrid,
    DxEditing,
    DxExport,
    DxLoadPanel,
    DxPaging,
    DxScrolling,
    DxSorting,
} from 'devextreme-vue/data-grid';

import { DxDropDownBox } from 'devextreme-vue/drop-down-box';
import { DxTreeList } from 'devextreme-vue/tree-list';
import { checkHasEditData, getAiToolbar, undoEditData } from '@/components/utils/ai-toolbar-generator';
import { addNewRowData, createFilterArray, getAiDataSource } from '@/components/utils/ai-data-source-generator';
import {
    getDistribution,
    getDistributionLookup,
    setDistribution,
} from '@/views/mped/services/mped-amortization-api-service';
import { amortizationDistributionColumns, mvpLookupColumns } from '@/views/mped/services/mped-amortization-service';
import MpedCalculationPeriodComponent
    from '@/views/mped/views/calculation/component/mped-calculation-period-component.vue';
import { DxCheckBox } from 'devextreme-vue';

let lookupData = [];
let toolbarButtons = {};
const dataGridRef = ref();
const columns = amortizationDistributionColumns(lookupData, 'chooseMvp', 'startDate', 'endDate');
const gridColumns = mvpLookupColumns;
const checkbox = ref(false);

onBeforeMount(async () => {
    const response = await getDistributionLookup();
    lookupData = response.data;
});

const dataSource = getAiDataSource(
    {
        dataFn: getDistribution,
        key: 'id',
        remoteOperation: true,
        customFilterFn: () => {
            if (checkbox.value !== true) {
                return createFilterArray([['end_date', '=', null]]);
            }
            return [];
        },
        insertFn: (values) => {
            return setDistribution({ insertDataArgs: [values] });
        },
        updateFn: (id, values) => {
            return setDistribution({ updateDataArgs: [{ id, ...values }] });
        },
        removeFn: (id) => {
            return setDistribution({ deleteDataArgs: [{ id }] });
        },
    },
);

const onToolbarPreparing = (e) => {
    const filters = [{
        location: 'before',
        template: 'checkbox',
    }];
    toolbarButtons = getAiToolbar(e, {
        customs: filters,
        refresh: {
            event: reloadData,
        },
        add: {
            event: addData,
        },
        save: {
            event: (e) => {
                dataGridRef.value.instance.saveEditData();
            },
        },
        undo: {
            event: (e) => {
                undoEditData(dataGridRef.value.instance);
            },
        },
    });
};

const reloadData = () => {
    dataSource.reload();
};

const addData = (e) => {
    addNewRowData({
        dxInstance: dataGridRef.value.instance,
        sourceData: {
            id: e.id,
            thermal_city: 0,
            thermal_suburb: 0,
            electricity_city: 0,
            electricity_suburb: 0,
            ...e,
        },
        fields: ['id', 'start_date', 'end_date', 'mvp_code', 'thermal_city', 'thermal_suburb', 'department_id',
            'electricity_city', 'electricity_suburb'],
    });
    dataSource.reload();
};

const onContentReady = (e) => {
    checkHasEditData(dataGridRef.value.instance, toolbarButtons);
};

const onCellPrepared = (e) => {
    if (e.rowType !== 'data' || e.columnIndex != 0) {
        return;
    }
};

const updateChanges = (changesData, refresh = true) => {
    const instance = dataGridRef.value.instance;
    const changes = instance.option('editing.changes') || [];

    const existingChangeIndex = changes.findIndex(c => c.key === changesData.key);

    if (existingChangeIndex >= 0) {
        changes[existingChangeIndex].data = {
            ...changes[existingChangeIndex].data,
            ...changesData.data,
        };
    } else {
        changes.push(changesData);
    }

    if (refresh) {
        instance.option('editing.changes', changes);
        instance.refresh();
    }
};

const handleDateChange = async (e, rowData, fieldName) => {
    const newDate = e.pop();

    if (rowData.data.id && newDate !== rowData.data[fieldName]) {
        updateChanges({
            type: 'update',
            key: rowData.data.id,
            data: {
                [fieldName]: newDate ? newDate : null,
            },
        }, false);
    } else {
        rowData.data[fieldName] = newDate;
    }
};

const onStartDateChanged = async (e, rowData) => {
    await handleDateChange(e, rowData, 'start_date');
};

const onEndDateChanged = async (e, rowData) => {
    await handleDateChange(e, rowData, 'end_date');
};

const onMvpSelectionChanged = async (e, rowData) => {
    if (!e?.row?.data || !rowData?.data) return;
    const { mvp_code: newMvpCode } = e.row.data;
    rowData.data.mvp_code = newMvpCode;

    if (dataGridRef.value.instance?.refresh) {
        dataGridRef.value.instance.refresh();
    }

    updateChanges({
        type: 'update',
        key: rowData.key,
        data: {
            mvp_code: newMvpCode,
        },
    }, true);
};

const onCheckboxChanged = (e) => {
    checkbox.value = e.value;
    reloadData();
};
</script>

